import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Opt {
  value: string
  label: string
}

/** Lightweight labelled dropdown (entity / period end-month). Hand-rolled for portability. */
export function Dropdown({
  label,
  value,
  options,
  onChange,
  align = 'left',
}: {
  label: string
  value: string
  options: Opt[]
  onChange: (v: string) => void
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const f = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', f)
    return () => document.removeEventListener('mousedown', f)
  }, [])
  const cur = options.find((o) => o.value === value)
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2.5 py-1.5 transition-colors hover:bg-stone-50 dark:border-stone-800 dark:bg-stone-900 dark:hover:bg-stone-800"
      >
        <span className="text-[10px] font-medium uppercase tracking-wider text-stone-400">{label}</span>
        <span className="text-xs font-medium text-stone-700 dark:text-stone-200">{cur?.label ?? value}</span>
        <ChevronDown className="h-3.5 w-3.5 text-stone-400" />
      </button>
      {open && (
        <div
          role="listbox"
          className={cn(
            'absolute z-30 mt-1 max-h-64 w-52 overflow-auto rounded-xl border border-stone-200 bg-white py-1 shadow-lg dark:border-stone-800 dark:bg-stone-900',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {options.map((o) => (
            <button
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              onClick={() => {
                onChange(o.value)
                setOpen(false)
              }}
              className={cn(
                'block w-full px-3 py-1.5 text-left text-xs transition-colors hover:bg-stone-50 dark:hover:bg-stone-800',
                o.value === value ? 'font-semibold text-stone-900 dark:text-white' : 'text-stone-600 dark:text-stone-300',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
